import * as Sentry from '@sentry/react';
import {
  CurrentStep,
  Domain,
  HtmlStepperForm,
  HtmlStepperFormData,
  stepperDirectUpdateTypes,
  stepperEventToUpdateType,
  stepperModalUpdateTypes,
  StepperPreviewEvent,
  StepperPreviewEventEditField,
  StepperPreviewEventEditHtmlAction,
  StepperPreviewEventName,
  StepperPreviewEventSelectImage,
  StepperPreviewEventSelectThumbnail,
  StepperPreviewEventStepChanged,
  Website,
} from '@superfunnel/types';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAgentChat } from '../../../contexts/AgentChatContext';
import { useHtmlStepperUpdater } from '../../../contexts/HtmlStepperUpdaterContext';
import {
  buildPreviewUrl,
  PREVIEW_ROOT,
  sendMessageToIframe,
} from '../../../utils';

export interface UseHtmlStepperIframeMessagingProps {
  quizId: string;
  formId: string;
  form: HtmlStepperForm | null;
  website: Website | null;
  setRawForm: (form: HtmlStepperForm | null) => void;
  // Preview URL params
  accountId: string;
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  dynamicPageId: string | null;
  variantId: string | null;
  websiteId: string | null;
  previewRef: RefObject<HTMLIFrameElement | null>;
  onOpenEditHtmlActionModal?: (
    data: StepperPreviewEventEditHtmlAction['data'],
  ) => void;
  onOpenEditFieldModal?: (data: StepperPreviewEventEditField['data']) => void;
  onOpenSelectImageModal?: (
    data: StepperPreviewEventSelectImage['data'],
  ) => void;
  onOpenSelectThumbnailModal?: (
    data: StepperPreviewEventSelectThumbnail['data'],
  ) => void;
  isVisualEditMode: boolean;
  setIsVisualEditMode?: (isVisual: boolean) => void;
  editorMode: 'editor' | 'preview' | 'logic' | 'results';
  currentStep: CurrentStep;
  onStepChanged?: (currentStep: CurrentStep) => void;
  isEditable: boolean;
  /** Incremented when local form data changes (add/duplicate/delete step) to trigger a re-send */
  dataVersion: number;
}

export interface UseHtmlStepperIframeMessagingReturn {
  ready: number;
  resetReady: () => void;
  iframeId: string;
  iframeSrc: string | null;
  sendMessage: (message: any) => void;
  goToStep: (step: CurrentStep) => void;
}

export function useHtmlStepperIframeMessaging({
  quizId,
  formId,
  form,
  website,
  previewRef,
  accountId,
  primaryDomain,
  dynamicPageId,
  variantId,
  websiteId,
  onOpenEditHtmlActionModal,
  onOpenEditFieldModal,
  onOpenSelectImageModal,
  onOpenSelectThumbnailModal,
  isVisualEditMode,
  setIsVisualEditMode,
  editorMode,
  currentStep,
  onStepChanged,
  isEditable,
  dataVersion,
}: UseHtmlStepperIframeMessagingProps): UseHtmlStepperIframeMessagingReturn {
  const [readyCount, setReadyCount] = useState(0);
  const setReady = () => setReadyCount((prev) => prev + 1);
  const resetReady = () => setReadyCount(0);
  const iframeIdRef = useRef<string>(`html-stepper-preview-${formId}`);
  const currentStepRef = useRef<CurrentStep>(currentStep);

  // Keep the ref in sync with the prop
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const { createStepperUpdate, applyFormChange, applyChangesSync } =
    useHtmlStepperUpdater();

  const { addPromptContext } = useAgentChat();

  const sendMessage = useCallback(
    (message: any) => {
      sendMessageToIframe(message, previewRef);
    },
    [previewRef],
  );

  // Handle messages from iframe
  const handlePreviewEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;

      const iframe = previewRef.current?.contentWindow;
      if (event.source !== iframe) return;

      try {
        const parsed = JSON.parse(event.data) as StepperPreviewEvent;
        const eventName = parsed.eventName as StepperPreviewEventName;

        console.log('[HTML_STEPPER_PREVIEW] event', eventName, parsed);

        if (parsed.eventName === 'ready') {
          setReady();
          return;
        }

        if (parsed.eventName === 'elementSelected') {
          // Add the selected element to prompt contexts
          const elementData = parsed.data;
          const contextId = `element-${Date.now()}-${Math.random()}`;
          addPromptContext({
            id: contextId,
            type: 'element',
            elementPath: elementData.elementPath,
            elementHTML: elementData.elementHTML,
            elementText: elementData.elementText,
            elementTag: elementData.elementTag,
            // Stepper-specific context
            target: elementData.target?.type,
            stepId:
              elementData.target?.type === 'step'
                ? elementData.target.stepId
                : elementData.target?.type === 'finalStep'
                  ? elementData.target.finalStepId
                  : undefined,
          });
          // Notify parent to exit visual mode
          setIsVisualEditMode?.(false);
          return;
        }

        // Handle step changed event from iframe
        if (parsed.eventName === 'stepChanged') {
          const { currentStep: newCurrentStep } = (
            parsed as StepperPreviewEventStepChanged
          ).data;
          onStepChanged?.(newCurrentStep);
          return;
        }

        // Map event to update type
        const updateType = stepperEventToUpdateType[eventName];
        if (!updateType) {
          console.log(
            '[HTML_STEPPER_PREVIEW] No update type for event:',
            eventName,
          );
          return;
        }

        // Handle modal-based events
        if (stepperModalUpdateTypes.has(updateType)) {
          if (parsed.eventName === 'editHtmlAction') {
            onOpenEditHtmlActionModal?.(parsed.data);
          } else if (parsed.eventName === 'editField') {
            onOpenEditFieldModal?.(parsed.data);
          } else if (parsed.eventName === 'selectImage') {
            onOpenSelectImageModal?.(parsed.data);
          } else if (parsed.eventName === 'selectThumbnail') {
            onOpenSelectThumbnailModal?.(parsed.data);
          }
          return;
        }

        // Handle direct-apply events
        if (stepperDirectUpdateTypes.has(updateType) && form?.development) {
          // Type narrowing: at this point, parsed must have data
          if (
            parsed.eventName !== 'htmlElementChanged' &&
            parsed.eventName !== 'editImageSettings'
          ) {
            return;
          }

          const { target, elementPath, content } = parsed.data;

          // Store current state for undo
          const before = JSON.parse(
            JSON.stringify(form.development),
          ) as HtmlStepperFormData;

          const update = createStepperUpdate(
            formId,
            target,
            elementPath,
            content,
            updateType,
            before,
          );

          if (update) {
            applyFormChange(update);
          }
        }
      } catch (e) {
        Sentry.captureException(e);
        console.error('[HTML_STEPPER_PREVIEW] Error parsing event:', e);
      }
    },
    [
      addPromptContext,
      applyFormChange,
      createStepperUpdate,
      form?.development,
      formId,
      onOpenEditFieldModal,
      onOpenEditHtmlActionModal,
      onOpenSelectImageModal,
      onOpenSelectThumbnailModal,
      onStepChanged,
      previewRef,
      setIsVisualEditMode,
    ],
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handlePreviewEvent);
    return () => {
      window.removeEventListener('message', handlePreviewEvent);
    };
  }, [handlePreviewEvent]);

  // Send form data to iframe when ready or when data changes
  useEffect(() => {
    if (!form || !readyCount) return;

    // Apply any pending updates to get the latest form data
    const updatedDevelopment = applyChangesSync(form.development);
    const formWithPendingChanges = {
      ...form,
      development: updatedDevelopment,
    };

    sendMessage({
      eventName: 'websiteUpdated',
      formId: formId,
      currentStep: currentStepRef.current,
      website: {
        ...website?.development,
        forms: {
          [formId]: formWithPendingChanges,
        },
        quizzes: {
          [quizId]: {
            quizId: quizId,
            formIdsWeights: { [formId]: 100 },
            forms: { [formId]: formWithPendingChanges },
          },
        },
      },
    });
  }, [
    applyChangesSync,
    dataVersion,
    form,
    formId,
    quizId,
    readyCount,
    sendMessage,
    website?.development,
  ]);

  useEffect(() => {
    if (isVisualEditMode) {
      sendMessage({
        eventName: 'previewConfigUpdated',
        isEditable: false,
      });
      sendMessage({
        eventName: 'enterSelectionMode',
      });
    } else {
      sendMessage({
        eventName: 'previewConfigUpdated',
        isEditable: isEditable && editorMode === 'editor',
      });
      sendMessage({
        eventName: 'exitSelectionMode',
      });
    }
  }, [isVisualEditMode, sendMessage, isEditable, editorMode]);

  // Send isEditable to iframe (only editable in editor mode and when editing is allowed)
  useEffect(() => {
    if (!readyCount) return;
    sendMessage({
      eventName: 'previewConfigUpdated',
      isEditable: isEditable && editorMode === 'editor',
    });
  }, [readyCount, editorMode, isEditable, sendMessage]);

  // Explicitly navigate to a step in the iframe
  const goToStep = useCallback(
    (step: CurrentStep) => {
      if (!readyCount) return;
      sendMessage({
        eventName: 'goToStep',
        currentStep: step,
      });
    },
    [readyCount, sendMessage],
  );

  const iframeSrc = useMemo(() => {
    if (!primaryDomain) return null;
    if (!websiteId) return null;
    if (!dynamicPageId) return null;
    if (!variantId) return null;

    return (
      buildPreviewUrl(
        primaryDomain.data().domain,
        accountId,
        dynamicPageId,
        variantId,
        websiteId,
        '', // dynamicPageWebsitePreviewToken,
        true,
      ) + '&systemPreview=true&pageType=quiz'
    );
  }, [accountId, primaryDomain, dynamicPageId, variantId, websiteId]);

  return {
    ready: readyCount,
    resetReady,
    iframeId: iframeIdRef.current,
    iframeSrc,
    sendMessage,
    goToStep,
  };
}
